import { useEffect, useRef, useState } from 'react';

import L from 'leaflet';
import PropTypes from 'prop-types';

import TextInput from '#globalComponents/Forms/TextInput';

import { useGeocode } from '../../../models/prospectQueries/queries';

import 'leaflet/dist/leaflet.css';

const RADIUS_MIN = 1;
const RADIUS_MAX = 200;
// Radius used only to draw the preview circle while no radius is set. It is
// never written back into the filter — an unset radius stays unset (null),
// which the backend treats as "search without a geo-radius".
const PREVIEW_RADIUS_KM = 25;
const GEOCODE_DEBOUNCE_MS = 600;

// Fallback map centers (approx. country centroids) when a location hasn't been
// entered or geocoding returned nothing. Keys match the popup's COUNTRY_OPTIONS.
const COUNTRY_CENTER = {
	Deutschland: [51.1633, 10.4477],
	Österreich: [47.5162, 14.5501],
	Schweiz: [46.8182, 8.2275],
	Niederlande: [52.1326, 5.2913],
	Frankreich: [46.2276, 2.2137],
	Italien: [41.8719, 12.5674],
	Polen: [51.9194, 19.1451],
	Belgien: [50.5039, 4.4699],
};
const DEFAULT_CENTER = COUNTRY_CENTER.Deutschland;

/**
 * Standort radius control: a Leaflet map with a draggable center pin + live
 * radius circle, driven by a stepless 1–200 km slider and numeric input.
 *
 * The center is resolved from the typed city/postal code via the backend
 * geocoder (same source as the actual search); it falls back to the country
 * centroid while the lookup is pending or empty.
 */
export const RadiusMap = ({ location, country, radius, onRadius }) => {
	const elRef = useRef(null);
	const mapRef = useRef(null);
	const circleRef = useRef(null);
	const markerRef = useRef(null);
	// Whether the user has dragged the pin — once they have, we stop auto-recentering
	// from geocoding so we don't yank their manual placement away.
	const pinMovedRef = useRef(false);

	// Whether the user has actually set a radius. An empty string / null / 0
	// means "no radius" — we render a faint preview circle but send nothing.
	const hasRadius = radius !== null && radius !== undefined && radius !== '' && Number(radius) > 0;
	// Value driving the circle + slider. Falls back to the preview radius purely
	// for rendering; it is never persisted unless the user changes it.
	const r = Math.round(hasRadius ? Number(radius) : PREVIEW_RADIUS_KM);
	const setR = (v) => onRadius(Math.round(Math.max(RADIUS_MIN, Math.min(RADIUS_MAX, v))));
	// Clearing the numeric input resets the filter to "no radius".
	const clearRadius = () => onRadius(null);

	// Debounce the typed location so we don't geocode on every keystroke.
	const [debouncedLocation, setDebouncedLocation] = useState(location || '');
	useEffect(() => {
		const t = setTimeout(() => setDebouncedLocation(location || ''), GEOCODE_DEBOUNCE_MS);
		return () => clearTimeout(t);
	}, [location]);

	const { data: geo } = useGeocode(debouncedLocation, country, Boolean(debouncedLocation));

	// Fit the map to the current radius circle, but only once it has a real size.
	const fitToCircle = () => {
		const map = mapRef.current;
		const circle = circleRef.current;
		if (!map || !circle) return;
		const size = map.getSize();
		if (!size || size.x === 0 || size.y === 0) return;
		try {
			map.fitBounds(circle.getBounds().pad(0.15));
		} catch {
			/* not ready yet */
		}
	};

	// Init the map once.
	useEffect(() => {
		if (!elRef.current || mapRef.current) return undefined;
		const center = COUNTRY_CENTER[country] || DEFAULT_CENTER;

		const map = L.map(elRef.current, {
			zoomControl: true,
			attributionControl: true,
			scrollWheelZoom: false,
			dragging: true,
			doubleClickZoom: true,
			zoomAnimation: false,
		});
		map.setView(center, 10);
		L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
			maxZoom: 19,
			attribution: '&copy; OpenStreetMap',
		}).addTo(map);

		const circle = L.circle(center, {
			radius: r * 1000,
			color: '#7b66ff',
			weight: 2,
			fillColor: '#9584ff',
			fillOpacity: 0.14,
			// Dashed + faint while no radius is set, to signal it's only a preview.
			dashArray: hasRadius ? null : '4 6',
			opacity: hasRadius ? 1 : 0.4,
		}).addTo(map);

		const pin = L.divIcon({
			className: '',
			html: '<div style="width:18px;height:18px;border-radius:50%;background:#7b66ff;border:3px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.35);"></div>',
			iconSize: [18, 18],
			iconAnchor: [9, 9],
		});
		const marker = L.marker(center, { draggable: true, icon: pin }).addTo(map);
		marker.on('drag', () => circle.setLatLng(marker.getLatLng()));
		marker.on('dragend', () => {
			pinMovedRef.current = true;
			fitToCircle();
		});

		mapRef.current = map;
		circleRef.current = circle;
		markerRef.current = marker;

		const t = setTimeout(() => {
			map.invalidateSize();
			fitToCircle();
		}, 280);
		return () => {
			clearTimeout(t);
			map.remove();
			mapRef.current = null;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// React to radius changes (size + preview styling for the unset state).
	useEffect(() => {
		const circle = circleRef.current;
		if (!circle) return;
		circle.setRadius(r * 1000);
		circle.setStyle({
			dashArray: hasRadius ? null : '4 6',
			opacity: hasRadius ? 1 : 0.4,
		});
		fitToCircle();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [r, hasRadius]);

	// Recenter when geocoding resolves the typed location (unless the user has
	// dragged the pin manually).
	useEffect(() => {
		const circle = circleRef.current;
		const marker = markerRef.current;
		if (!circle || !marker || pinMovedRef.current) return;
		if (typeof geo?.lat !== 'number' || typeof geo?.lng !== 'number') return;
		const center = [geo.lat, geo.lng];
		circle.setLatLng(center);
		marker.setLatLng(center);
		fitToCircle();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [geo?.lat, geo?.lng]);

	// A fresh location input re-enables auto-recentering.
	useEffect(() => {
		pinMovedRef.current = false;
	}, [location]);

	return (
		<div className="space-y-2">
			<div className="flex items-center justify-between">
				<label className="text-xs text-bgBase-800">Radius (optional)</label>
				<div className="flex items-center gap-1">
					<TextInput
						type="number"
						min={RADIUS_MIN}
						max={RADIUS_MAX}
						value={hasRadius ? r : ''}
						placeholder="—"
						onChange={(e) => (e.target.value === '' ? clearRadius() : setR(Number(e.target.value)))}
						className="!w-20 !px-2.5 text-right tabular-nums"
					/>
					<span className="text-[11px] text-bgBase-800">km</span>
				</div>
			</div>
			<input
				type="range"
				min={RADIUS_MIN}
				max={RADIUS_MAX}
				step={1}
				value={r}
				onChange={(e) => setR(Number(e.target.value))}
				className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-600"
			/>
			<div className="flex justify-between text-[10px] text-bgBase-800">
				<span>{RADIUS_MIN} km</span>
				<span>{RADIUS_MAX} km</span>
			</div>
			<div
				ref={elRef}
				className="relative z-0 w-full rounded-lg border border-gray-200 overflow-hidden bg-gray-100"
				style={{ height: 200 }}
			/>
			<p className="text-[10.5px] text-bgBase-800 leading-snug">
				{hasRadius
					? 'Pin ziehen, um den Mittelpunkt zu verschieben. Der Umkreis aktualisiert sich live.'
					: 'Kein Radius gesetzt – es wird ohne Umkreis nur nach Standort gesucht. Wert eingeben oder Regler nutzen, um einen Umkreis festzulegen.'}
			</p>
		</div>
	);
};

RadiusMap.propTypes = {
	location: PropTypes.string,
	country: PropTypes.string,
	radius: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	onRadius: PropTypes.func.isRequired,
};
